import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Icon } from '@iconify/react'

// antd
import { Row, Input } from 'antd'

// components
import AssigneeSelectorButton from './AssigneeSelectorButton'
import TypeSelector from '../../taskRefactored/components/typeSelector'

// styles
import styles from '../projectDetail.module.css'

const { TextArea } = Input

const TASK_TYPE_OPTIONS = [
    { label: 'Task', value: 'Task' },
    { label: 'Bug', value: 'Bug' },
]

const isOverlayPickerClick = (target) => {
    if (!target?.closest) return false
    return !!(
        target.closest('.ant-popover') ||
        target.closest('.ant-select-dropdown') ||
        target.closest('[class*="assigneeSelector"]') ||
        target.closest('[class*="assignToMe"]') ||
        target.closest('[class*="sectionPanel"]')
    )
}

const getTitleInputElement = (inputRef) =>
    inputRef?.current?.resizableTextArea?.textArea ||
    inputRef?.current?.input ||
    inputRef?.current ||
    null

const CreateTaskCard = ({
    createTask,
    setCreateTask,
    filteredAssignees,
    createTaskFromBoard,
    initialCreateTask,
    projectId,
    status,
    position,
    className,
    contentClassName,
    headerClassName,
    actionsClassName,
    inputClassName,
    taskListKind = 'support',
    taskType = 'Task',
    typeDisabled = false,
}) => {
    const cardRef = useRef(null)
    const inputRef = useRef(null)
    const inputValueRef = useRef(createTask?.text || '')
    const assigneeIdRef = useRef(createTask?.assigneeId ?? null)
    const assigneeDataRef = useRef(createTask?.assigneeData ?? null)
    const taskTypeRef = useRef(taskType)
    const isSubmittingRef = useRef(false)
    const [selectedType, setSelectedType] = useState(taskType)

    useEffect(() => {
        assigneeIdRef.current = createTask?.assigneeId ?? null
    }, [createTask?.assigneeId])

    useEffect(() => {
        assigneeDataRef.current = createTask?.assigneeData ?? null
    }, [createTask?.assigneeData])

    useEffect(() => {
        if (!createTask?.open) return
        const nextType = taskType || 'Task'
        taskTypeRef.current = nextType
        setSelectedType(nextType)
    }, [createTask?.open, taskType])

    const buildCreateOptions = useCallback(
        () => ({
            assigneeId:
                assigneeIdRef.current ??
                createTask?.assigneeId ??
                createTask?.assigneeData?._id ??
                null,
            assigneeData:
                assigneeDataRef.current ?? createTask?.assigneeData ?? null,
            type: taskTypeRef.current || taskType || 'Task',
        }),
        [createTask?.assigneeId, createTask?.assigneeData, taskType]
    )

    const submitCreateTask = useCallback(
        (taskName) => {
            if (isSubmittingRef.current) return
            const trimmed = String(taskName || '').trim()
            if (!trimmed) return
            isSubmittingRef.current = true
            Promise.resolve(
                createTaskFromBoard(trimmed, buildCreateOptions())
            ).finally(() => {
                isSubmittingRef.current = false
            })
        },
        [createTaskFromBoard, buildCreateOptions]
    )

    const focusTitleInput = useCallback(() => {
        requestAnimationFrame(() => {
            getTitleInputElement(inputRef)?.focus?.()
        })
    }, [])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOverlayPickerClick(event.target)) return
            if (cardRef?.current && !cardRef?.current.contains(event.target)) {
                if (
                    inputValueRef.current &&
                    inputValueRef.current.trim().length > 0
                ) {
                    submitCreateTask(inputValueRef.current.trim())
                } else {
                    setCreateTask(initialCreateTask)
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [
        initialCreateTask,
        setCreateTask,
        submitCreateTask,
        createTask?.loading,
    ])

    // Enter creates even after focus moves to assignee/type controls
    useEffect(() => {
        if (!createTask?.open) return undefined

        const handleGlobalEnter = (event) => {
            if (event.key !== 'Enter' || event.shiftKey || event.isComposing) {
                return
            }
            if (isOverlayPickerClick(event.target)) return

            const title = String(inputValueRef.current || '').trim()
            if (!title) return

            const titleInput = getTitleInputElement(inputRef)
            const target = event.target
            const isInCard = cardRef.current?.contains(target)
            const isTitleInput = titleInput && target === titleInput

            // Don't steal Enter from unrelated page inputs
            if (
                !isTitleInput &&
                !isInCard &&
                (target?.tagName === 'INPUT' ||
                    target?.tagName === 'TEXTAREA' ||
                    target?.isContentEditable)
            ) {
                return
            }

            event.preventDefault()
            setCreateTask((prev) => ({
                ...prev,
                text: title,
            }))
            submitCreateTask(title)
        }

        document.addEventListener('keydown', handleGlobalEnter)
        return () => {
            document.removeEventListener('keydown', handleGlobalEnter)
        }
    }, [createTask?.open, setCreateTask, submitCreateTask])

    useEffect(() => {
        if (!createTask?.open) {
            isSubmittingRef.current = false
        }
        if (createTask?.open) {
            focusTitleInput()
        }
    }, [createTask?.open, focusTitleInput])

    const handleInputChange = (e) => {
        inputValueRef.current = e.target.value.trimStart()
    }

    const handleAssigneeChange = useCallback(
        (selected) => {
            const assigneeId = selected?._id || selected || null
            assigneeIdRef.current = assigneeId
            assigneeDataRef.current = selected || null
            setCreateTask((prevState) => ({
                ...prevState,
                assigneeId,
                assigneeData: selected || null,
                text: inputValueRef.current || prevState?.text || '',
            }))
            focusTitleInput()
        },
        [setCreateTask, focusTitleInput]
    )

    const handleTypeChange = useCallback(
        (_name, value) => {
            const nextType = value || 'Task'
            taskTypeRef.current = nextType
            setSelectedType(nextType)
            setCreateTask((prevState) => ({
                ...prevState,
                type: nextType,
                text: inputValueRef.current || prevState?.text || '',
            }))
            focusTitleInput()
        },
        [setCreateTask, focusTitleInput]
    )

    const getInitials = (name) => {
        if (!name) return 'U'
        const parts = name.split(' ')
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase()
        }
        return name.substring(0, 2).toUpperCase()
    }

    const getAssigneeColor = (name) => {
        if (!name) return '#e2e8f0'
        const colors = [
            '#85BBF9',
            '#6366f1',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#06b6d4',
        ]
        const index = name.charCodeAt(0) % colors.length
        return colors[index]
    }

    const selectedAssigneeData = useMemo(() => {
        if (createTask?.assigneeData) {
            return createTask.assigneeData
        }
        if (!createTask?.assigneeId) return null

        return filteredAssignees.find(
            (data) => data?.value === createTask?.assigneeId
        )
    }, [filteredAssignees, createTask?.assigneeId, createTask?.assigneeData])

    const rootClassName = [styles.createTaskCard, className]
        .filter(Boolean)
        .join(' ')
        .trim()
    const contentClass = [styles.createTaskCardContent, contentClassName]
        .filter(Boolean)
        .join(' ')
        .trim()
    const headerClass = [styles.createTaskHeader, headerClassName]
        .filter(Boolean)
        .join(' ')
        .trim()
    const actionsClass = [styles.createTaskActions, actionsClassName]
        .filter(Boolean)
        .join(' ')
        .trim()
    const inputClass = [styles.createTaskInput, inputClassName]
        .filter(Boolean)
        .join(' ')
        .trim()

    const isMainCreateCard = taskListKind === 'main'

    return (
        <div
            className={rootClassName}
            ref={cardRef}
            style={{
                opacity: 1,
                pointerEvents: 'auto',
                position: 'relative',
                marginBottom: position === 'top' ? '8px' : '0',
            }}
        >
            {/* {createTask?.loading && (
                <div className={styles.loaderCreateTask}></div>
            )} */}
            <div className={contentClass}>
                <div className={headerClass}>
                    {taskListKind === 'support' && (
                        <Icon
                            icon="simple-line-icons:check"
                            width={16}
                            height={16}
                            style={{ color: '#94a3b8' }}
                        />
                    )}
                    <TextArea
                        className={inputClass}
                        placeholder={
                            selectedType === 'Bug'
                                ? 'Add Bug'
                                : 'Write a task name'
                        }
                        autoFocus
                        defaultValue={createTask?.text}
                        onChange={handleInputChange}
                        ref={inputRef}
                        variant="borderless"
                        autoSize={{ minRows: 1, maxRows: 5 }}
                    />
                </div>
                <Row
                    align="middle"
                    justify="flex-start"
                    className={actionsClass}
                >
                    <AssigneeSelectorButton
                        selectedAssignee={selectedAssigneeData}
                        onSelect={handleAssigneeChange}
                        projectId={projectId}
                        filteredAssignees={filteredAssignees}
                        getAssigneeColor={getAssigneeColor}
                        getInitials={getInitials}
                        taskListKind={taskListKind}
                    />
                    {isMainCreateCard && (
                        <div
                            data-ignore-click
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <TypeSelector
                                value={selectedType}
                                placeholder="Select type"
                                typeOptions={TASK_TYPE_OPTIONS}
                                name="type"
                                compact
                                dashedBorder
                                disabled={typeDisabled}
                                updateDetails={handleTypeChange}
                            />
                        </div>
                    )}
                </Row>
            </div>
        </div>
    )
}

export default CreateTaskCard
